import React, { createContext, useState } from 'react'
import { getLocallyStoredCurrentPage } from './helper'
import GenericModal from '../../components/GenericModal'
import { useNavigate } from 'react-router-dom'
import ROUTES from '../../consts/paths'
import { ButtonType } from '../../components/GenericButton/styles'
import theme from '../../theme'
import { SetupWizardSteps } from './constants'

interface SetupTourContextType {
  isWizardOpen: boolean
  setIsWizardOpen: (isOpen: boolean) => void
  currPage: number
  setCurrPage: (page: number) => void
  isLastPageSeeable: boolean
  setIsLastPageSeeable: (isSeeable: boolean) => void
  newlyAddedTermsToProject: string[]
  setNewlyAddedTermsToProject: (terms: string[]) => void
  setIsLogoutConfirmationOpen: (isOpen: boolean) => void
}

const SetupTourContext = createContext<SetupTourContextType | undefined>(undefined)

export const SetupTourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isWizardOpen, setIsWizardOpen] = useState<boolean>(false)
  const [currPage, setCurrPage] = useState<number>(getLocallyStoredCurrentPage())
  const [isLastPageSeeable, setIsLastPageSeeable] = useState<boolean>(false)
  const [newlyAddedTermsToProject, setNewlyAddedTermsToProject] = useState<string[]>([])
  const [isLogoutConfirmationOpen, setIsLogoutConfirmationOpen] = useState<boolean>(false)
  const navigate = useNavigate()

  const handleLogOut = async (): Promise<void> => {
    setCurrPage(SetupWizardSteps.GETTING_STARTED)
    setIsLastPageSeeable(false)
    setNewlyAddedTermsToProject([])
    setIsLogoutConfirmationOpen(false)
    navigate(ROUTES.LOGOUT)
  }

  return (
    <SetupTourContext.Provider
      value={{
        isWizardOpen,
        setIsWizardOpen,
        currPage,
        setCurrPage,
        isLastPageSeeable,
        setIsLastPageSeeable,
        newlyAddedTermsToProject,
        setNewlyAddedTermsToProject,
        setIsLogoutConfirmationOpen
      }}
    >
      <GenericModal
        isOpen={isLogoutConfirmationOpen}
        onClose={() => { setIsLogoutConfirmationOpen(false) }}
        title='Are you sure you want to log out?'
        confirmActionText='Log Out'
        confirmActionType={ButtonType.Destructive}
        destructiveButtonText='Cancel'
        destructiveButtonType={ButtonType.Secondary}
        onConfirm={handleLogOut}
        modalWidth={theme.spacing(136)}

      >
        You have started the setup wizard. Logging out now will discard all your progress. Are you sure you want to continue?
      </GenericModal>
      {children}
    </SetupTourContext.Provider>
  )
}

export const useSetupTour = (): SetupTourContextType => {
  return React.useContext(SetupTourContext) as SetupTourContextType
}
